import React, { useState, useEffect } from 'react';
import { Question } from '../../types/game';
import { useTheme } from '../../contexts/ThemeContext';

interface QuestionScreenProps {
  question: Question;
  score: number;
  hasAnswered: boolean;
  onAnswerSelect: (choiceIdx: number) => void;
}

const QuestionScreen: React.FC<QuestionScreenProps> = ({ question, score, hasAnswered, onAnswerSelect }) => {
  const { theme } = useTheme();
  const [timeRemaining, setTimeRemaining] = useState(question.secondsLeft || 30);
  const [timerPercentage, setTimerPercentage] = useState(100);
  const [shuffledChoices, setShuffledChoices] = useState<Array<{ text: string; originalIndex: number }>>([]);
  const [selectedIdx, setSelectedIdx] = useState<number | null>(null);

  useEffect(() => {
    // Shuffle choices
    const originalChoices = [...question.choices];
    const indexed = originalChoices.map((choice, index) => ({
      text: choice,
      originalIndex: index
    }));

    // Fisher-Yates shuffle
    for (let i = indexed.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indexed[i], indexed[j]] = [indexed[j], indexed[i]];
    }

    setShuffledChoices(indexed);
    setTimeRemaining(question.secondsLeft || 30);
    setTimerPercentage(100);
    setSelectedIdx(null);
  }, [question]);

  useEffect(() => {
    const totalSeconds = question.secondsLeft || 30;
    const timer = setInterval(() => {
      setTimeRemaining((prev) => {
        if (prev <= 0) {
          clearInterval(timer);
          return 0;
        }
        const newTime = prev - 1;
        setTimerPercentage((newTime / totalSeconds) * 100);
        return newTime;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [question.secondsLeft]);

  const handleAnswerClick = (originalIdx: number, displayIdx: number) => {
    if (hasAnswered) return;
    setSelectedIdx(displayIdx);
    onAnswerSelect(originalIdx);
  };

  const emojis = [
    theme.emojis.choice1,
    theme.emojis.choice2,
    theme.emojis.choice3,
    theme.emojis.choice4
  ];

  const choiceColors = [
    theme.colors.choice1,
    theme.colors.choice2,
    theme.colors.choice3,
    theme.colors.choice4
  ];

  const choiceTextColors = [
    theme.colors.choice1Text || '#ffffff',
    theme.colors.choice2Text || '#ffffff',
    theme.colors.choice3Text || '#ffffff',
    theme.colors.choice4Text || '#ffffff'
  ];

  const getButtonClasses = (displayIdx: number) => {
    // Ensure minimum 44px touch target for mobile accessibility
    const baseClasses = `w-full px-4 py-3 font-bold transform min-h-[60px] flex items-center justify-center text-center touch-manipulation`;

    const shapeClasses = {
      'rounded': 'rounded-2xl',
      'pill': 'rounded-full',
      'square': 'rounded-none'
    }[theme.layout.buttonShape];

    const shadowClasses = theme.effects.buttonShadows ? 'shadow-lg' : '';
    const hoverClasses = !hasAnswered ? theme.animations.buttonHover + ' hover:shadow-xl active:scale-95' : 'opacity-50 cursor-not-allowed';
    const selectedClasses = selectedIdx === displayIdx ? theme.animations.buttonPress : '';

    return `${baseClasses} ${shapeClasses} ${shadowClasses} ${hoverClasses} ${selectedClasses} ${theme.animations.transition}`;
  };

  const getButtonStyle = (displayIdx: number) => {
    const color = choiceColors[displayIdx];
    const textColor = choiceTextColors[displayIdx];

    // Check if it's a gradient or solid color
    if (color.includes('gradient')) {
      return { background: color, color: textColor };
    } else {
      return { backgroundColor: color, color: textColor };
    }
  };

  const cardClasses = theme.effects.glassmorphism ? 'glassmorphic' : 'bg-gray-800/80';
  const textSizeClasses = `text-${theme.typography.questionSize}`;
  const answerSizeClasses = `text-${theme.typography.answerSize}`;

  return (
    <div className="min-h-screen flex flex-col p-4 animate-[fadeIn_0.3s_ease-in]">
      {/* Compressed Header */}
      <div className="flex justify-between items-center mb-4">
        <div className={`${cardClasses} rounded-xl px-4 py-2 shadow-lg`}>
          <div className="flex items-center gap-2">
            <span className="text-xl">{theme.emojis.score}</span>
            <span className={`font-bold text-white ${theme.typography.scoreSize}`}>
              Score: <span style={{ color: theme.colors.accent }}>{score}</span>
            </span>
          </div>
        </div>

        <div className={`${cardClasses} rounded-xl px-4 py-2 shadow-lg`}>
          <div className="flex items-center gap-2">
            <span className={`font-bold text-white ${theme.typography.scoreSize}`}>Time:</span>
            <span className="font-bold" style={{ color: theme.colors.accent }}>{timeRemaining}s</span>
          </div>
        </div>
      </div>

      {/* Compact Timer */}
      <div className="mb-6">
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full transition-all duration-1000 ease-linear"
            style={{
              width: `${timerPercentage}%`,
              background: `linear-gradient(to right, ${theme.colors.timerHigh}, ${theme.colors.timerMid}, ${theme.colors.timerLow})`
            }}
          />
        </div>
      </div>

      {/* Question and Answers */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className={`${cardClasses} rounded-3xl p-8 max-w-4xl w-full shadow-2xl mb-8`}>
          <h2 className={`${textSizeClasses} md:text-3xl font-bold text-center mb-8 leading-relaxed`} style={{ color: theme.colors.textPrimary }}>
            #{question.index} - {question.text}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {shuffledChoices.map((choice, displayIdx) => (
              <button
                key={displayIdx}
                onClick={() => handleAnswerClick(choice.originalIndex, displayIdx)}
                disabled={hasAnswered}
                className={getButtonClasses(displayIdx)}
                style={getButtonStyle(displayIdx)}
              >
                <span className="text-xl mr-2">{emojis[displayIdx]}</span>
                <span className={`flex-1 ${answerSizeClasses} sm:text-base`}>{choice.text}</span>
              </button>
            ))}
          </div>

          {hasAnswered && (
            <div className="text-center mt-6 font-bold text-xl" style={{ color: theme.colors.correct }}>
              Answer submitted!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuestionScreen;